import {
  ActionIcon,
  Center,
  Container,
  Group,
  Image,
  Loader,
  Modal,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import slugify from 'react-slugify';
import { useSwipeable } from 'react-swipeable';

import { ProductUI, useGetProductDetail } from '@/api/useGetProducts';
import { TopMenu } from '@/components/TopMenu';

import { AddProduct } from '../components/AddProduct';
import { ImageContainer } from '../components/ImageContainer';
import { PriceInfo } from '../components/PriceInfo';

export const Product = () => {
  const { slug } = useParams();

  const theme = useMantineTheme();
  const breakpoint = useMediaQuery(`(min-width: ${theme.breakpoints.md}px)`);
  const { productDetail, categories, images, isLoading, attributes } =
    useGetProductDetail({
      productName: slugify(slug),
    });

  const {
    name,
    url,
    description,
    price,
    unit,
    weight,
    promotionValue,
    pricePerKg,
    promotionPrice,
    promotionPricePerKg,
    dateStart,
    dateEnd,
    minimum,
    productId,
  } = productDetail || ({} as ProductUI);

  const galleryImages = useMemo(
    () => Array.from(new Set([url, ...(images || [])].filter(Boolean))),
    [images, url]
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const hasMultipleImages = galleryImages.length > 1;

  const showPreviousImage = useCallback(() => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    );
  }, [galleryImages.length]);

  const showNextImage = useCallback(() => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
    );
  }, [galleryImages.length]);

  useEffect(() => {
    setSelectedImageIndex(0);
    setIsViewerOpen(false);
  }, [productId]);

  useEffect(() => {
    if (!isViewerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && hasMultipleImages) showPreviousImage();
      if (event.key === 'ArrowRight' && hasMultipleImages) showNextImage();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [hasMultipleImages, isViewerOpen, showNextImage, showPreviousImage]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (hasMultipleImages) showNextImage();
    },
    onSwipedRight: () => {
      if (hasMultipleImages) showPreviousImage();
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  if (isLoading)
    return (
      <Center style={{ height: '100vh' }}>
        <Loader color="dark" />
      </Center>
    );
  return (
    <>
      <TopMenu />
      <Container size="xl" py="100px">
        <Stack>
          <Group spacing="xl" position="center" align={'start'} grow>
            <Stack
              spacing={0}
              style={{ flexGrow: 0, minWidth: breakpoint ? '220px' : '100%' }}
            >
              <ImageContainer
                url={url}
                name={name}
                promotionValue={promotionValue}
                onClick={() => {
                  setSelectedImageIndex(0);
                  setIsViewerOpen(true);
                }}
              />
            </Stack>
            <Stack
              spacing={'xs'}
              style={{ minWidth: breakpoint ? '220px' : '100%' }}
            >
              <Stack spacing={0}>
                <Title>{name}</Title>
                <Text size="sm" color="gray">
                  Categorias:
                  {categories?.map(({ name, categoryId }, index) => (
                    <Text component="span" key={categoryId || index}>
                      {name}
                      {index === categories.length - 1 ? '' : ', '}
                    </Text>
                  ))}
                </Text>
              </Stack>
              <PriceInfo
                unit={unit}
                dateEnd={dateEnd}
                dateStart={dateStart}
                price={price}
                pricePerKg={pricePerKg}
                promotionPrice={promotionPrice}
                promotionPricePerKg={promotionPricePerKg}
              />
              <AddProduct
                minimum={minimum}
                productId={productId}
                unit={unit}
                weight={weight}
              />
              <Text size="xs">
                1 unidade = aprox.
                {weight > 1000 ? `${weight / 1000}kg` : `${weight}g`}
              </Text>

              <>
                <Table
                  my="md"
                  striped
                  style={{ width: breakpoint ? '60%' : '100%' }}
                >
                  <tbody>
                    {attributes?.map(({ attribute_name, attribute_value }) => (
                      <tr key={attribute_value}>
                        <td>{attribute_name}</td>
                        <td>{attribute_value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Text align="justify">
                  {description?.split('\r\n\r\n').map((text) => (
                    <Text pb={'md'} key={text}>
                      {text}
                    </Text>
                  ))}
                </Text>
              </>
            </Stack>
          </Group>
          <Tabs
            color={'green'}
            pt={breakpoint ? '40px' : 0}
            px={breakpoint ? 'xl' : 0}
            defaultValue={'gallery'}
          >
            <Tabs.List>
              <Tabs.Tab value="gallery">
                <Title order={3}>Galeria</Title>
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="gallery" pt="xs">
              {galleryImages.length ? (
                <Stack spacing="md">
                  <Group spacing="xs">
                    {galleryImages.map((galleryImage, index) => (
                      <Image
                        key={galleryImage || index}
                        src={galleryImage}
                        alt={`${name} miniatura ${index + 1}`}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setIsViewerOpen(true);
                        }}
                        style={{
                          width: breakpoint ? '120px' : '90px',
                          cursor: 'pointer',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                        }}
                      />
                    ))}
                  </Group>

                  <Modal
                    opened={isViewerOpen}
                    onClose={() => setIsViewerOpen(false)}
                    withCloseButton={false}
                    centered
                    size="95vw"
                    overlayColor="#000"
                    overlayOpacity={0.85}
                    overlayBlur={2}
                    styles={{
                      modal: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                      },
                      body: {
                        padding: 0,
                      },
                    }}
                  >
                    <Stack spacing="md">
                      <Group
                        noWrap
                        position="center"
                        align="center"
                        spacing={breakpoint ? 'md' : 'xs'}
                      >
                        {hasMultipleImages ? (
                          <ActionIcon
                            variant="filled"
                            color="dark"
                            size={breakpoint ? 'xl' : 'lg'}
                            radius="xl"
                            onClick={showPreviousImage}
                            aria-label="Imagem anterior"
                          >
                            {'<'}
                          </ActionIcon>
                        ) : null}
                        <div {...swipeHandlers}>
                          <Image
                            src={galleryImages[selectedImageIndex]}
                            alt={`${name} ${selectedImageIndex + 1}`}
                            fit="contain"
                            style={{
                              maxHeight: '85vh',
                              maxWidth: 'calc(100vw - 160px)',
                              width: 'auto',
                              margin: '0 auto',
                            }}
                          />
                        </div>
                        {hasMultipleImages ? (
                          <ActionIcon
                            variant="filled"
                            color="dark"
                            size={breakpoint ? 'xl' : 'lg'}
                            radius="xl"
                            onClick={showNextImage}
                            aria-label="Próxima imagem"
                          >
                            {'>'}
                          </ActionIcon>
                        ) : null}
                      </Group>
                    </Stack>
                  </Modal>
                </Stack>
              ) : null}
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </>
  );
};
